/**
 * sseStream.js
 * Buffered Server-Sent Events reader for the streaming AI endpoints.
 *
 * Why buffering matters: a fetch body stream splits on TCP chunk boundaries, not on message
 * boundaries. Decoding each chunk and splitting on "\n\n" without carrying the remainder
 * forward silently discards the partial event at the end of every chunk — tokens vanish from
 * the middle of the answer. This keeps the trailing partial in a buffer until it completes.
 */

/**
 * @param {Response} response  a fetch() response with a readable body
 * @param {(evt: {content?: string, reasoning?: string, error?: string, done?: boolean}) => void} onEvent
 * @returns {Promise<void>}
 */
export async function readSSE(response, onEvent) {
  if (!response || !response.body) throw new Error('Response has no readable body');
  const emit = typeof onEvent === 'function' ? onEvent : () => {};

  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  let inThink = false;

  /* Route text through, splitting any <think>...</think> span into the reasoning channel. */
  const emitContent = (text) => {
    let rest = text;
    while (rest) {
      if (inThink) {
        const close = rest.indexOf('</think>');
        if (close === -1) { emit({ reasoning: rest }); return; }
        if (close > 0) emit({ reasoning: rest.slice(0, close) });
        rest = rest.slice(close + 8);
        inThink = false;
      } else {
        const open = rest.indexOf('<think>');
        if (open === -1) { emit({ content: rest }); return; }
        if (open > 0) emit({ content: rest.slice(0, open) });
        rest = rest.slice(open + 7);
        inThink = true;
      }
    }
  };

  const handlePayload = (payload) => {
    if (payload === '[DONE]') { emit({ done: true }); return; }
    let obj;
    try {
      obj = JSON.parse(payload);
    } catch (e) {
      return; // partial or non-JSON keep-alive frame
    }

    if (obj.error) {
      emit({ error: obj.error.message || String(obj.error) });
      return;
    }

    const choice = obj.choices && obj.choices[0];
    const delta = choice && choice.delta;
    if (delta) {
      if (delta.reasoning) emit({ reasoning: delta.reasoning });
      if (delta.content) emitContent(delta.content);
      return;
    }
    if (choice && choice.message && choice.message.content) {
      emitContent(choice.message.content);
      return;
    }
    if (typeof obj.content === 'string') emitContent(obj.content);
  };

  const drain = (block) => {
    for (const rawLine of block.split('\n')) {
      const line = rawLine.trim();
      if (!line || line.startsWith(':')) continue;      // comment / keep-alive
      if (!line.startsWith('data:')) continue;
      handlePayload(line.slice(5).trim());
    }
  };

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    const parts = buffer.split('\n\n');
    buffer = parts.pop() || '';                          // keep the trailing partial
    for (const part of parts) drain(part);
  }

  // Flush whatever the server left without a final blank line.
  buffer += decoder.decode();
  if (buffer.trim()) drain(buffer);
}
